/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button } from 'react-bootstrap-v5';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import ApexCharts, { ApexOptions } from 'apexcharts';
import axios from 'axios';
import moment from 'moment';
import { userInfo } from 'os';
import { getCSS, getCSSVariableValue } from 'src/_metronic/assets/ts/_utils';
import { useAppSelector } from 'src/services/redux/app/store';
import { useCampaignAddNote, useCampaignAddStatus, useCampaignLeadList } from '../service';

type Props = {
  className: string;
  chartData: any;
};

type CompanySuitability = {
  id: number;
  name: string;
  isSuitable: boolean | null;
};

type ResultData = {
  id: number;
  lead_id: number;
  status: boolean | null;
  note: string;
  created_at: string;
  company_code: string;
};

type CampaignLead = {
  id: number;
  name_lastname: string;
  phone: string;
  organisation_date: string;
  created_at: string;
  updated_at: string;
  note: string | null;
  isDateSuitable?: boolean | null;
  companySuitabilities?: CompanySuitability[];
  result_data?: ResultData[];
};

const CampaignManagement: React.FC<Props> = ({ className, chartData }) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const [campaignLeads, setCampaignLeads] = useState<CampaignLead[]>([]);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState<CampaignLead | null>(null);
  const [noteText, setNoteText] = useState('');
  const [trigger, setTrigger] = useState(false);
  const { user: userData } = useAppSelector((state: any) => state.auth);

  const onSuccessFetchNote = (response: any, variables: any) => {
    toast.success('Not başarıyla eklendi.');

    // Not ekledikten sonra UI'ı güncelle
    setCampaignLeads((prevLeads) => {
      return prevLeads.map((lead) => {
        // Bu işlemde güncellediğimiz lead mi?
        if (lead.id === variables.lead_id) {
          const updatedResultData = [...(lead.result_data || [])];
          const existingIndex = updatedResultData.findIndex((result) => result.company_code === userData?.name);

          if (existingIndex !== -1) {
            updatedResultData[existingIndex] = {
              ...updatedResultData[existingIndex],
              note: variables.note,
            };
          } else {
            updatedResultData.push({
              id: Date.now(), // geçici id
              lead_id: variables.lead_id,
              status: null,
              note: variables.note,
              created_at: new Date().toISOString(),
              company_code: userData?.name || '',
            });
          }

          return {
            ...lead,
            result_data: updatedResultData,
          };
        }
        return lead;
      });
    });
  };

  const onSuccessFetchStatus = (response: any, variables: any) => {
    toast.success('Uygunluk durumu başarıyla eklendi.');

    // UI'ı güncellemek için doğrudan state'i güncelliyoruz
    setCampaignLeads((prevLeads) => {
      return prevLeads.map((lead) => {
        // Bu işlemde güncellediğimiz lead mi? Eğer öyleyse güncelliyoruz
        if (lead.id === variables.lead_id) {
          // result_data'yı güncelleyeceğiz
          const updatedResultData = [...(lead.result_data || [])];

          // Kullanıcının girişini kontrol et
          const existingIndex = updatedResultData.findIndex((result) => result.company_code === userData?.name);

          // Eğer bu kullanıcı için bir kayıt zaten varsa, güncelle
          if (existingIndex !== -1) {
            updatedResultData[existingIndex] = {
              ...updatedResultData[existingIndex],
              status: variables.status,
            };
          } else {
            // Yoksa yeni bir kayıt ekle
            updatedResultData.push({
              id: Date.now(), // geçici id
              lead_id: variables.lead_id,
              status: variables.status,
              note: '',
              created_at: new Date().toISOString(),
              company_code: userData?.name || '',
            });
          }

          // Lead'i güncelliyoruz
          return {
            ...lead,
            isDateSuitable: variables.status,
            result_data: updatedResultData,
          };
        }
        return lead;
      });
    });
  };

  const onErrorFetch = (e: any) => e?.response?.data?.message && toast.error(e?.response?.data?.message);
  const { data: campaign_leadList, refetch: campaign_leadRefetch } = useCampaignLeadList(onErrorFetch);
  const { isLoading: addNoteIsLoading, mutate: mutateAddNote, reset: addNoteReset } = useCampaignAddNote(onSuccessFetchNote, onErrorFetch);
  const { isLoading: addStatusIsLoading, mutate: mutateAddStatus, reset: addStatusReset } = useCampaignAddStatus(onSuccessFetchStatus, onErrorFetch);

  useEffect(() => {
    if (campaign_leadList) {
      setCampaignLeads(
        campaign_leadList.map((lead: CampaignLead) => {
          // Use the result_data array directly
          const resultData = lead.result_data || [];

          // Determine isDateSuitable based on resultData status
          let isDateSuitable: boolean | null = null;
          if (resultData?.length > 0) {
            // Find matching result for current user's company regardless of note
            const matchingResult = resultData?.find((result) => result?.company_code === userData?.name);
            isDateSuitable = matchingResult ? matchingResult?.status : null;
          }

          return {
            ...lead,
            isDateSuitable: isDateSuitable,
          };
        }),
      );
    }
  }, [campaign_leadList, trigger, userData?.name]);

  const handleDateSuitabilityChange = async (id: number, value: boolean | null) => {
    try {
      await mutateAddStatus({
        lead_id: id,
        status: value,
      });
      // Artık refetch yapmaya gerek yok çünkü UI'ı doğrudan güncelliyoruz
      // campaign_leadRefetch() satırını kaldırıyoruz
      // setTrigger(!trigger) de kaldırılabilir çünkü artık yerel durumu güncelliyoruz
    } catch (error) {
      console.error('Uygunluk durumu güncellenirken bir hata oluştu:', error);
      // Hata durumunda refetch yapabiliriz
      campaign_leadRefetch();
    }
  };

  const handleOpenNoteModal = (lead: CampaignLead) => {
    setSelectedLead(lead);
    setNoteText(lead.note || '');
    setShowNoteModal(true);
  };

  const handleSaveNote = async () => {
    if (!selectedLead) return;

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        throw new Error('REACT_APP_API_URL is not defined in your environment.');
      }

      await mutateAddNote({
        company_id: 1, //todo: company id dinamik olmalı
        lead_id: selectedLead.id,
        note: noteText,
      });

      // Update the note in our local state
      setCampaignLeads(campaignLeads.map((lead) => (lead.id === selectedLead.id ? { ...lead, note: noteText } : lead)));
      setShowNoteModal(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Not kaydedilirken bir hata oluştu');
    }
  };

  useEffect(() => {
    if (!chartRef.current || !chartData) {
      return;
    }

    const height = parseInt(getCSS(chartRef.current, 'height'));

    const chart = new ApexCharts(chartRef.current, getChartOptions(height, chartData));
    if (chart) {
      chart.render();
    }

    return () => {
      if (chart) {
        chart.destroy();
      }
    };
  }, [chartRef, chartData]);

  // Function to get the background color based on suitability
  const getSuitabilityBackgroundColor = (isSuitable: boolean | null): string => {
    if (isSuitable === null) return 'bg-secondary';
    return isSuitable ? 'bg-success' : 'bg-danger';
  };

  // Function to render company suitability badges from result_data
  const renderCompanySuitabilityBadges = (resultData: ResultData[] | undefined) => {
    // Standard company codes we want to display
    const standardCompanies = ['Feyza', 'Alya', 'Elif Eylül', 'Sinerji'];

    // If no result data, show all companies in gray
    if (!resultData || resultData?.length === 0) {
      return (
        <>
          {standardCompanies.map((companyCode, index) => (
            <div key={index} className="d-inline-block text-center me-2" style={{ width: '30px', height: '30px' }}>
              <span
                data-tooltip-id={`tooltip-${companyCode}-${index}aa`}
                className="d-flex align-items-center justify-content-center fw-bolder bg-secondary text-white rounded"
                style={{ width: '100%', height: '100%', fontSize: '14px' }}
              >
                {companyCode.charAt(0)}
              </span>
              <Tooltip id={`tooltip-${companyCode}-${index}aa`} content={'Not bulunamadı.'} />
            </div>
          ))}
        </>
      );
    }

    // Create a map of company codes to their status
    const companyStatusMap: Record<string, boolean | null> = {};
    resultData.forEach((result) => {
      companyStatusMap[result.company_code] = result.status;
    });

    // Render all standard companies, using result data where available
    return standardCompanies.map((companyCode, index) => {
      // Determine if we have data for this company
      const hasData = companyCode in companyStatusMap;
      // Get the status if data exists, otherwise it's null (will be gray)
      const status = hasData ? companyStatusMap[companyCode] : null;
      const result = resultData.find((r) => r.company_code === companyCode);
      const note = result ? result.note : null;

      return (
        <div key={index} className="d-inline-block text-center me-2" style={{ width: '30px', height: '30px' }}>
          <span
            data-tooltip-id={`tooltip-${companyCode}-${result?.id}`}
            className={`d-flex align-items-center justify-content-center fw-bolder ${status === null ? 'bg-secondary' : status ? 'bg-success' : 'bg-danger'} text-white rounded`}
            style={{ width: '100%', height: '100%', fontSize: '14px' }}
          >
            {companyCode.charAt(0)}
          </span>
          <Tooltip id={`tooltip-${companyCode}-${result?.id}`} content={note || 'Not bulunamadı.'} />
        </div>
      );
    });
  };

  return (
    <div className={`card ${className}`}>
      {/* begin::Header */}
      <div className="card-header border-0 pt-5">
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label fw-bolder fs-3 mb-1">Kampanya Talepleri</span>
        </h3>
      </div>
      {/* end::Header */}

      {/* begin::Body */}
      <div className="card-body">
        {/* begin::Campaign Leads Table */}
        <div className="mt-5">
          <div className="table-responsive">
            <table className="table align-middle table-row-dashed fs-6 gy-5">
              <thead>
                <tr className="text-start text-muted fw-bolder fs-7 text-uppercase gs-0">
                  <th>Ad Soyad</th>
                  <th>Telefon</th>
                  <th>Organizasyon Tarihi</th>
                  <th>Not</th>
                  <th>Tarih Uygun mu?</th>
                  <th>Firma Uygunlukları</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {campaignLeads?.length > 0 ? (
                  campaignLeads?.map((lead) => (
                    <tr key={lead.id}>
                      <td>{lead.name_lastname}</td>
                      <td>{lead.phone}</td>
                      <td>{moment(lead.organisation_date).locale('tr').format('DD MMMM YYYY')}</td>
                      <td>{lead.note || '-'}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="btn-group" role="group">
                            <button
                              type="button"
                              className={`btn btn-sm ${lead.isDateSuitable === true ? 'btn-success' : 'btn-light-success'}`}
                              onClick={() => handleDateSuitabilityChange(lead.id, true)}
                            >
                              Uygun
                            </button>
                            <button
                              type="button"
                              className={`btn btn-sm ${lead.isDateSuitable === false ? 'btn-danger' : 'btn-light-danger'}`}
                              onClick={() => handleDateSuitabilityChange(lead.id, false)}
                            >
                              Uygun Değil
                            </button>
                          </div>
                          <div className="ms-3">
                            {lead.isDateSuitable === null && <span className="badge badge-light-secondary fw-bolder">Seçilmedi</span>}
                            {lead.isDateSuitable === true && <span className="badge badge-light-success fw-bolder">Uygun</span>}
                            {lead.isDateSuitable === false && <span className="badge badge-light-danger fw-bolder">Uygun Değil</span>}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">{renderCompanySuitabilityBadges(lead.result_data)}</div>
                      </td>
                      <td>
                        <button
                          type="button"
                          className={`btn btn-sm ${
                            lead?.result_data?.find((result) => result?.company_code === userData?.name && result?.note?.length > 0) ? 'btn-light-warning' : 'btn-light-primary'
                          }`}
                          onClick={() => handleOpenNoteModal(lead)}
                        >
                          {lead?.result_data?.find((result) => result?.company_code === userData?.name && result?.note?.length > 0) ? 'Notu Düzenle' : 'Not Ekle'}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center">
                      Henüz veri bulunmamaktadır
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {/* end::Campaign Leads Table */}
      </div>
      {/* end::Body */}

      {/* Note Modal */}
      <Modal show={showNoteModal} onHide={() => setShowNoteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Not Ekle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">
              <span className="px-4 py-1 fw-bolder bg-warning text-black">{selectedLead?.name_lastname}</span> müşterisi için not ekliyorsunuz...
            </label>
            <textarea className="form-control" rows={4} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Not ekleyin..." />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNoteModal(false)}>
            İptal
          </Button>
          <Button variant="primary" onClick={handleSaveNote}>
            Not Ekle
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export { CampaignManagement };

function getRandomNumber(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function getChartOptions(height: number, chartData: any): ApexOptions {
  const labelColor = getCSSVariableValue('--bs-gray-500');
  const borderColor = getCSSVariableValue('--bs-gray-200');
  const baseColor = getCSSVariableValue('--bs-info');

  const colors = [
    {
      bs_light: getCSSVariableValue('--bs-info'),
      bs: getCSSVariableValue('--bs-info'),
    },
    {
      bs_light: getCSSVariableValue('--bs-warning'),
      bs: getCSSVariableValue('--bs-warning'),
    },
    {
      bs_light: getCSSVariableValue('--bs-success'),
      bs: getCSSVariableValue('--bs-success'),
    },
    {
      bs_light: getCSSVariableValue('--bs-danger'),
      bs: getCSSVariableValue('--bs-danger'),
    },
    {
      bs_light: getCSSVariableValue('--bs-secondary'),
      bs: getCSSVariableValue('--bs-secondary'),
    },
  ];

  const series = chartData?.map((cash: any, index: number) => ({
    name: cash?.account_name,
    data: cash?.merged_data?.map(
      (merged: any) =>
        Number(merged?.total_price_payments) +
        Number(merged?.total_price_income_expenses) +
        Number(merged?.total_price_current_account) +
        Number(merged?.total_transfer_price_expenses) +
        Number(merged?.total_transfer_price_income),
    ),
  }));

  const categories = chartData?.map((cash: any) => cash?.merged_data?.map((merged: any) => moment(merged?.transaction_date).fromNow()));
  const uniqueDates = Array.from(new Set(categories.flat()));
  const colorsData = chartData?.map((cash: any, index: number) => {
    const randomIndex = getRandomNumber(0, colors?.length - 1);
    return colors[randomIndex];
  });
  return {
    series: series,
    chart: {
      fontFamily: 'inherit',
      type: 'area',
      height: 350,
      toolbar: {
        show: true,
      },
    },
    plotOptions: {},
    legend: {
      show: true,
    },
    dataLabels: {
      enabled: false,
    },
    fill: {
      type: 'solid',
      opacity: 0.3,
    },
    stroke: {
      curve: 'smooth',
      show: true,
      width: 3,
      colors: colorsData?.map((color: any) => color?.bs),
    },
    xaxis: {
      categories: uniqueDates,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
      crosshairs: {
        position: 'front',
        stroke: {
          color: baseColor,
          width: 1,
          dashArray: 3,
        },
      },
      tooltip: {
        enabled: false,
        formatter: undefined,
        offsetY: 0,
        style: {
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
    },
    states: {
      normal: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      hover: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
          value: 0,
        },
      },
    },
    tooltip: {
      style: {
        fontSize: '12px',
      },
      y: {
        formatter: function (val) {
          return Number(val)?.toLocaleString('tr-TR') + ' TL';
        },
      },
    },
    colors: colorsData?.map((color: any) => color?.bs_light),
    grid: {
      borderColor: borderColor,
      strokeDashArray: 4,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    markers: {
      strokeColors: baseColor,
      strokeWidth: 3,
    },
  };
}
